import { ListFilter, CalendarIcon } from 'lucide-react'
import { type HTMLAttributes, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'

import { Button } from '@/modules/shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/modules/shared/components/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
} from '@/modules/shared/components/ui/form'
import { Search } from '@/modules/shared/components/ui/search'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { Label } from '@/modules/shared/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/modules/shared/components/ui/radio-group'
import { usePromotionSearchInputStore } from '../stores/promotion-search-input.store'
import { PROMOTION_SEARCH_PARAMETERS } from '../constants/promotion-search-input.constants'
import { Popover, PopoverContent, PopoverTrigger } from '@/modules/shared/components/ui/popover'
import { Calendar } from '@/modules/shared/components/ui/calendar'
import { cn } from '@/modules/shared/lib/utils'

type PromotionSearchInputComponentProps = HTMLAttributes<HTMLInputElement> & {
	onSubmitRequest: (payload: PayloadSearch["payload"]) => void
	disabled?: boolean
	defaultInputValue?: string
}

export function PromotionSearchInputComponent({
	onSubmitRequest,
	defaultInputValue,
	...rest
}: PromotionSearchInputComponentProps) {
	const [isOpen, setIsOpen] = useState(false)
	const onClose = () => setIsOpen(false)
	const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)

	const form = useForm({
		values: {
			inputValue: defaultInputValue,
			dataInicio: '',
			dataFim: '',
		},
	})

	const {
		defaultFilter,
		actions: { setDefaultFilter },
	} = usePromotionSearchInputStore()

	// Check if current filter is for date
	const isDateFilter = defaultFilter === 'data'

	// Update form values when date range changes
	useEffect(() => {
		if (dateRange?.from && dateRange?.to) {
			form.setValue('dataInicio', format(dateRange.from, 'yyyy-MM-dd'))
			form.setValue('dataFim', format(dateRange.to, 'yyyy-MM-dd'))
		} else if (dateRange?.from) {
			form.setValue('dataInicio', format(dateRange.from, 'yyyy-MM-dd'))
			form.setValue('dataFim', '')
		} else {
			form.setValue('dataInicio', '')
			form.setValue('dataFim', '')
		}
	}, [dateRange, form])

	const handleSubmitRequest = (data: {
		inputValue: string
		dataInicio: string
		dataFim: string
	}) => {
		// Definir tipo com base no filtro selecionado
		let filterType: 'igual' | 'contendo' = 'contendo'

		switch (defaultFilter) {
			case 'id_visita':
			case 'data':
				filterType = 'igual'
				break
			case 'p.razao_social_nome':
				filterType = 'contendo'
				break
		}

		// Use date range values if using date filter
		let inputValue = data.inputValue
		let inputValue2: string | undefined

		if (isDateFilter && data.dataInicio) {
			inputValue = data.dataInicio
			if (data.dataFim) {
				inputValue2 = data.dataFim
			}
		}

		const payload = {
			inputValue,
			inputValue2,
			filter: defaultFilter,
			filterType,
		}

		onSubmitRequest(payload)
	}

	const handleChangeFilter = (filter: string) => {
		// Reset form values when changing filter
		form.setValue('inputValue', '')
		form.setValue('dataInicio', '')
		form.setValue('dataFim', '')
		setDateRange(undefined)
		setDefaultFilter(filter)
		onClose()
	}

	const placeholderText =
		PROMOTION_SEARCH_PARAMETERS.find((value) => value.parameter === defaultFilter)?.text ||
		'Razão Social'

	return (
		<div className="flex gap-2 flex-1">
			<Button onClick={() => setIsOpen(true)} variant="secondary">
				<ListFilter size={16} />
			</Button>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmitRequest)}
					className="flex-1 md:flex-none md:w-96"
				>
					{isDateFilter ? (
						// Date range picker for "data" filter
						<div className="flex flex-1 w-full">
							<Popover>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										className={cn(
											"w-full justify-start text-left font-normal",
											!dateRange && "text-muted-foreground"
										)}
									>
										<CalendarIcon className="mr-2 h-4 w-4" />
										{dateRange?.from ? (
											dateRange.to ? (
												<>
													{format(dateRange.from, "dd/MM/yyyy", { locale: pt })} -{" "}
													{format(dateRange.to, "dd/MM/yyyy", { locale: pt })}
												</>
											) : (
												format(dateRange.from, "dd/MM/yyyy", { locale: pt })
											)
										) : (
											<span>Selecione um período</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-auto p-0" align="start">
									<Calendar
										mode="range"
										selected={dateRange}
										onSelect={(newDateRange) => {
											setDateRange(newDateRange)
											// Auto-submit when both dates are selected
											if (newDateRange?.from && newDateRange?.to) {
												setTimeout(() => form.handleSubmit(handleSubmitRequest)(), 100)
											}
										}}
										initialFocus
										locale={pt}
										numberOfMonths={1}
									/>
								</PopoverContent>
							</Popover>
						</div>
					) : (
						// Regular search input for other filters
						<FormField
							control={form.control}
							name="inputValue"
							render={({ field }) => (
								<FormItem className="flex flex-1 w-full">
									<FormControl>
										<Search
											{...field}
											onChange={(e) => {
												if (e.target.value.includes('&')) {
													if (e.target.value.includes(' &')) {
														field.onChange(e)
													} else {
														e.target.value = e.target.value.replace('&', '')
													}
												}
												field.onChange(e)
											}}
											min={3}
											onClick={(e) => {
												const inputElement = e.target as HTMLInputElement
												inputElement.select()
											}}
											value={field.value}
											onClickSearchIcon={form.handleSubmit(handleSubmitRequest)}
											placeholder={`Buscar por ${placeholderText}`}
											{...rest}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					)}
				</form>
			</Form>

			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Filtros</DialogTitle>
					</DialogHeader>
					<RadioGroup value={defaultFilter} className="space-y-3">
						{PROMOTION_SEARCH_PARAMETERS.map((item) => (
							<div key={item.id} className="flex items-center space-x-2">
								<RadioGroupItem
									onClick={() => handleChangeFilter(item.parameter)}
									key={item.id}
									id={item.id.toString()}
									value={item.parameter}
								/>
								<Label htmlFor={item.id.toString()} className="text-md">
									{item.text}
								</Label>
							</div>
						))}
					</RadioGroup>
				</DialogContent>
			</Dialog>
		</div>
	)
}